import React, { useState, useEffect } from 'react';
import {
    Container, Typography, Box, Paper, Grid, Button,
    Card, CardContent, CardActionArea, CircularProgress, Chip, Stack
} from '@mui/material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CodeIcon from '@mui/icons-material/Code';
import TerminalIcon from '@mui/icons-material/Terminal';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import PeopleIcon from '@mui/icons-material/People';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import ScienceIcon from '@mui/icons-material/Science';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const arborGreen = '#006D43';

const AdminOverview = () => {
    const { t } = useTranslation();
    const { currentUser, api } = useAuth();
    const navigate = useNavigate();

    const [userCount, setUserCount] = useState(null);
    const [logCount, setLogCount] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchOverviewStats = async () => {
            setLoading(true);
            try {
                const [usersRes, logsRes] = await Promise.all([
                    api.get('/admin/quota/users').catch(() => null),
                    api.get('/admin/logs', { params: { limit: 50 } }).catch(() => null)
                ]);

                if (usersRes?.data) {
                    setUserCount(usersRes.data.length);
                }
                if (logsRes?.data) {
                    setLogCount(logsRes.data.total || logsRes.data.entries?.length || 0);
                }
            } catch (err) {
                console.error("Failed to load admin overview stats", err);
            } finally {
                setLoading(false);
            }
        };

        if (currentUser?.is_super_admin) {
            fetchOverviewStats();
        }
    }, [api, currentUser]);

    const adminModules = [
        {
            title: t('Demo Data Generator'),
            description: t('Spin up fully populated demo ledgers with historical transactions, weekly cashflow, budgets, and famous stocks & ETFs.'),
            icon: <ScienceIcon sx={{ fontSize: 36, color: arborGreen }} />,
            path: '/admin/demo-data',
            tag: t('Demo & Testing')
        },
        {
            title: t('User Management'),
            description: t('Manage user accounts, security settings, service tiers, and AI feature quotas.'),
            icon: <PeopleIcon sx={{ fontSize: 36, color: arborGreen }} />,
            path: '/admin/quota',
            tag: t('Accounts & Quotas')
        },
        {
            title: t('Admin Scripts'),
            description: t('Execute database migrations, system maintenance routines, and custom backend scripts safely with sample inputs.'),
            icon: <CodeIcon sx={{ fontSize: 36, color: arborGreen }} />,
            path: '/admin/scripts',
            tag: t('Backend Operations')
        },
        {
            title: t('Server Logs Viewer'),
            description: t('Inspect live backend system logs, filter by severity level (ERROR, WARNING, INFO), and analyze full stack traces.'),
            icon: <TerminalIcon sx={{ fontSize: 36, color: arborGreen }} />,
            path: '/admin/logs',
            tag: t('Monitoring & Debugging')
        },
        {
            title: t('Data Import Tool'),
            description: t('Upload Excel files (PV Bookkeeping Main format) to auto-create missing categories and bulk populate ledger books.'),
            icon: <CloudUploadIcon sx={{ fontSize: 36, color: arborGreen }} />,
            path: '/admin/import',
            tag: t('System Utilities')
        },
        {
            title: t('Data Export Tool'),
            description: t('Export ledger data, user statistics, and system configurations for reporting, backups, or offline auditing.'),
            icon: <CloudDownloadIcon sx={{ fontSize: 36, color: arborGreen }} />,
            path: '/admin/export',
            tag: t('System Utilities')
        }
    ];

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
            {/* Header Banner */}
            <Paper
                elevation={0}
                sx={{
                    p: { xs: 3, sm: 4 },
                    mb: 4,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #004D2F 0%, #006D43 100%)',
                    color: '#ffffff',
                    position: 'relative',
                    overflow: 'hidden'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                    <AdminPanelSettingsIcon sx={{ fontSize: 40, color: '#A3F3C6' }} />
                    <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.02em', color: '#ffffff' }}>
                        {t('Admin Control Console')}
                    </Typography>
                </Box>
                <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)', maxWidth: 700, mb: 3 }}>
                    {t('Welcome to the central management hub. Manage user service tiers, execute maintenance scripts, inspect application logs, and run system data tools.')}
                </Typography>

                <Stack direction="row" spacing={2}>
                    <Button
                        variant="contained"
                        onClick={() => navigate('/')}
                        startIcon={<ExitToAppIcon />}
                        sx={{
                            bgcolor: '#ffffff',
                            color: '#004D2F',
                            fontWeight: 700,
                            '&:hover': { bgcolor: '#e6f7ef' }
                        }}
                    >
                        {t('Exit to Main App')}
                    </Button>
                </Stack>
            </Paper>

            {/* Quick Stat Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={4}>
                    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700 }}>
                                {t('TOTAL REGISTERED USERS')}
                            </Typography>
                            <PeopleIcon sx={{ color: arborGreen }} />
                        </Box>
                        {loading ? (
                            <CircularProgress size={24} sx={{ color: arborGreen }} />
                        ) : (
                            <Typography variant="h3" sx={{ fontWeight: 800, color: 'text.primary' }}>
                                {userCount !== null ? userCount : '—'}
                            </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            {t('Users across FREE, PRO, & ADMIN tiers')}
                        </Typography>
                    </Paper>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700 }}>
                                {t('RECENT SYSTEM LOGS')}
                            </Typography>
                            <TerminalIcon sx={{ color: arborGreen }} />
                        </Box>
                        {loading ? (
                            <CircularProgress size={24} sx={{ color: arborGreen }} />
                        ) : (
                            <Typography variant="h3" sx={{ fontWeight: 800, color: 'text.primary' }}>
                                {logCount !== null ? logCount : '—'}
                            </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            {t('Logged entries in system buffer')}
                        </Typography>
                    </Paper>
                </Grid>

                <Grid item xs={12} sm={12} md={4}>
                    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%', bgcolor: 'surface-container-lowest' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700 }}>
                                {t('SYSTEM STATUS')}
                            </Typography>
                            <Chip label={t('OPERATIONAL')} size="small" color="success" sx={{ fontWeight: 800 }} />
                        </Box>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: arborGreen, mt: 1 }}>
                            {t('All Services Active')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            {t('Ledger Service, Auth, APScheduler tasks online')}
                        </Typography>
                    </Paper>
                </Grid>
            </Grid>

            {/* Admin Modules Grid */}
            <Typography variant="h5" sx={{ fontWeight: 800, mb: 3, color: 'text.primary', letterSpacing: '-0.01em' }}>
                {t('Management Modules')}
            </Typography>

            <Grid container spacing={3}>
                {adminModules.map((mod) => (
                    <Grid item xs={12} sm={6} md={4} key={mod.title}>
                        <Card
                            elevation={0}
                            sx={{
                                height: '100%',
                                borderRadius: 3,
                                border: '1px solid',
                                borderColor: 'divider',
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                    borderColor: arborGreen,
                                    boxShadow: '0 12px 24px rgba(0, 109, 67, 0.08)',
                                    transform: 'translateY(-2px)'
                                }
                            }}
                        >
                            <CardActionArea
                                onClick={() => navigate(mod.path)}
                                sx={{ height: '100%', p: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'space-between' }}
                            >
                                <CardContent sx={{ width: '100%' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                        {mod.icon}
                                        <Chip label={mod.tag} size="small" variant="outlined" sx={{ fontWeight: 600, fontSize: '0.75rem' }} />
                                    </Box>
                                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: 'text.primary' }}>
                                        {mod.title}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                                        {mod.description}
                                    </Typography>
                                </CardContent>
                                <Box sx={{ p: 2, pt: 0, display: 'flex', alignItems: 'center', gap: 1, color: arborGreen, fontWeight: 700 }}>
                                    <span>{t('Open Module')}</span>
                                    <ArrowForwardIcon fontSize="small" />
                                </Box>
                            </CardActionArea>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </Container>
    );
};

export default AdminOverview;
